import type { DataProvider } from "@refinedev/core";
import { supabase } from "@/providers/supabaseClient";
import type { BffEnvelope, BffError } from "@/providers/types";
import { ENV } from "@/providers/env";

const BFF_BASE_URL = ENV.ADMIN_BFF_URL;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  return {
    Authorization: `Bearer ${session?.access_token ?? ""}`,
    "Content-Type": "application/json",
  };
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw {
      code: "network.error",
      message: "Ответ сервера не является JSON",
      statusCode: response.status,
      status: response.status,
    };
  }

  if (typeof json !== "object" || json === null) {
    throw {
      code: "server.error",
      message: "Неожиданный формат ответа сервера",
      statusCode: response.status,
      status: response.status,
    };
  }

  const envelope = json as Record<string, unknown>;

  if ("error" in envelope && envelope["error"] !== null) {
    const err = envelope["error"] as BffError;
    throw {
      code: err.code,
      message: err.message,
      details: err.details,
      traceId: err.traceId,
      statusCode: response.status,
      status: response.status,
    };
  }

  if (!response.ok) {
    throw {
      code: "server.error",
      message: "Ошибка сервера",
      statusCode: response.status,
      status: response.status,
    };
  }

  return (envelope as BffEnvelope<T> & { data: T }).data;
}

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, meta }) => {
    const cursor = meta?.["cursor"] as string | undefined;
    const params = new URLSearchParams();

    if (cursor) params.set("cursor", cursor);

    if (filters) {
      for (const f of filters) {
        if ("field" in f && "value" in f && f.field && f.value !== undefined) {
          params.set(f.field, String(f.value));
        }
      }
    }

    const queryString = params.toString();
    const url = `${BFF_BASE_URL}/admin/${resource}${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: await getAuthHeaders(),
    });

    const data = await parseEnvelope<{
      items?: unknown[];
      nextCursor?: string | null;
      total?: number;
    }>(response);

    const items = data.items ?? [];

    return {
      data: items as never[],
      total: data.total ?? items.length,
      meta: { nextCursor: data.nextCursor },
    };
  },

  getOne: async ({ resource, id }) => {
    const url = `${BFF_BASE_URL}/admin/${resource}/${String(id)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: await getAuthHeaders(),
    });

    const data = await parseEnvelope<Record<string, unknown>>(response);

    return { data: data as never };
  },

  create: async ({ resource, variables }) => {
    const url = `${BFF_BASE_URL}/admin/${resource}`;

    const response = await fetch(url, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify(variables),
    });

    const data = await parseEnvelope<Record<string, unknown>>(response);

    return { data: data as never };
  },

  update: async ({ resource, id, variables }) => {
    const url = `${BFF_BASE_URL}/admin/${resource}/${String(id)}`;

    const response = await fetch(url, {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: JSON.stringify(variables),
    });

    const data = await parseEnvelope<Record<string, unknown>>(response);

    return { data: data as never };
  },

  deleteOne: async ({ resource, id }) => {
    const url = `${BFF_BASE_URL}/admin/${resource}/${String(id)}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: await getAuthHeaders(),
    });

    const data = await parseEnvelope<Record<string, unknown>>(response);

    return { data: data as never };
  },

  custom: async ({ url, method, payload }) => {
    if (url.startsWith("http")) {
      const requestOrigin = new URL(url).origin;
      const bffOrigin = new URL(BFF_BASE_URL).origin;
      if (requestOrigin !== bffOrigin) {
        throw {
          code: "network.error",
          message: "Запрос к внешнему origin запрещён",
          statusCode: 400,
          status: 400,
        };
      }
    }

    const fullUrl = url.startsWith("http") ? url : `${BFF_BASE_URL}${url}`;

    const response = await fetch(fullUrl, {
      method: method.toUpperCase(),
      headers: await getAuthHeaders(),
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });

    const data = await parseEnvelope<Record<string, unknown>>(response);

    return { data: data as never };
  },

  getApiUrl: () => `${BFF_BASE_URL}/admin`,
};
